import { useState, useEffect, useCallback, useRef } from "react";
import apiClient from "../services/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { groupEmployeesByRole } from "../lib/sortUtils";
import { enqueueEntry, flushQueue, isNetworkError, readQueue, showLocalNotification } from "../lib/offlineQueue";
import { sendPresencePing } from "../lib/presence";
const createEmptyEntries = (count = 8) =>
  Array(count).fill(null).map(() => ({
    _uid: crypto.randomUUID(),
    employee_id: "",
    hours: "",
    materials: [
      { _uid: crypto.randomUUID(), material_id: "", quantity: "" },
      { _uid: crypto.randomUUID(), material_id: "", quantity: "" },
      { _uid: crypto.randomUUID(), material_id: "", quantity: "" },
    ],
  }));

export const useDailyEntry = () => {
  const { getAuthHeaders, user, logout } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [materialCategories, setMaterialCategories] = useState([]);
  const [previousEntries, setPreviousEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [editingEntryId, setEditingEntryId] = useState(null);

  // Material picker state
  const [materialPickerOpen, setMaterialPickerOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [materialPickerTarget, setMaterialPickerTarget] = useState({ empIndex: null, matIndex: null });

  // Hours entry state
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split("T")[0]);
  const [selectedJob, setSelectedJob] = useState("");
  const [notes, setNotes] = useState("");
  const [employeeEntries, setEmployeeEntries] = useState(createEmptyEntries());

  // Photo upload state
  const [photoJob, setPhotoJob] = useState("");
  const [uploadMode, setUploadMode] = useState("photo"); // "photo" | "receipt"
  const [uploading, setUploading] = useState(false);
  const [uploadConfirmOpen, setUploadConfirmOpen] = useState(false);
  const [uploadedPhotoCount, setUploadedPhotoCount] = useState(0);
  const [photoDescription, setPhotoDescription] = useState("");
  const [pendingFiles, setPendingFiles] = useState([]);
  const [descriptionDialogOpen, setDescriptionDialogOpen] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [jobsRes, employeesRes, materialsRes, entriesRes, settingsRes] = await Promise.all([
        apiClient.get(`/jobs/for-daily-entry`),
        apiClient.get(`/employees`),
        apiClient.get(`/materials`),
        apiClient.get(`/daily-entries`),
        apiClient.get(`/settings/options`),
      ]);
      setJobs(jobsRes.data);
      setEmployees(employeesRes.data.filter((e) => e.is_active));
      setMaterials(materialsRes.data);
      setPreviousEntries(entriesRes.data);
      // Derive categories from actual materials to ensure completeness
      const cats = [...new Set(materialsRes.data.map(m => m.category).filter(Boolean))];
      setMaterialCategories(cats);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Offline queue: send anything saved while out of signal, on open and whenever we come back online
  const [pendingCount, setPendingCount] = useState(() => readQueue().filter((q) => q.user_id === user?.id).length);
  const flushingRef = useRef(false);
  const flushPending = useCallback(async () => {
    if (!user?.id || !navigator.onLine || flushingRef.current) return;
    flushingRef.current = true;
    try {
      const { sent, failed } = await flushQueue(user.id);
      sent.forEach((item) => {
        toast.success(`Daily entry for ${item.job_name} submitted successfully!`, { duration: 8000 });
        showLocalNotification("Daily entry submitted", `Your entry for ${item.job_name} went through successfully.`);
      });
      failed.forEach(({ item, detail }) => toast.error(`Saved entry for ${item.job_name} was rejected: ${detail}`, { duration: 10000 }));
      setPendingCount(readQueue().filter((q) => q.user_id === user.id).length);
      if (sent.length) fetchData();
    } finally {
      flushingRef.current = false;
    }
  }, [user?.id, fetchData]);

  useEffect(() => {
    flushPending();
    window.addEventListener("online", flushPending);
    return () => window.removeEventListener("online", flushPending);
  }, [flushPending]);

  // Presence: where is the foreman when they open the app? Pre-select the job if we're clearly on one site.
  const [nearbyJobs, setNearbyJobs] = useState([]);
  useEffect(() => {
    if (user?.role !== "foreman") return;
    sendPresencePing("app_open").then((matches) => {
      setNearbyJobs(matches);
      if (matches.length === 1) {
        setSelectedJob((current) => current || matches[0].id);
        setPhotoJob((current) => current || matches[0].id);
      }
    });
  }, [user?.role]);

  // Handle ?edit= query param from Daily Report page
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const editId = params.get("edit");
    if (editId && !loading && !editingEntryId) {
      const entry = previousEntries.find(e => e.id === editId);
      if (entry) {
        handleEditEntry(entry);
        // Clean up URL without triggering navigation
        window.history.replaceState({}, "", window.location.pathname);
      } else if (previousEntries.length > 0) {
        // Entry not in local list (could be admin viewing foreman's entry), fetch it directly
        const loadEntry = async () => {
          try {
            const res = await apiClient.get(`/daily-entries/${editId}`);
            handleEditEntry(res.data);
            window.history.replaceState({}, "", window.location.pathname);
          } catch {
            toast.error("Could not load entry for editing");
          }
        };
        loadEntry();
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, previousEntries]);

  // Material picker functions
  const openMaterialPicker = (empIndex, matIndex) => {
    setMaterialPickerTarget({ empIndex, matIndex });
    setSelectedCategory("");
    setMaterialPickerOpen(true);
  };

  const selectMaterial = (materialId) => {
    const { empIndex, matIndex } = materialPickerTarget;
    if (empIndex !== null && matIndex !== null) {
      updateMaterial(empIndex, matIndex, "material_id", materialId);
    }
    setMaterialPickerOpen(false);
    setSelectedCategory("");
  };

  const getMaterialsInCategory = (category) => {
    return materials.filter(m => m.category === category);
  };

  const getSelectedMaterialName = (materialId) => {
    const material = materials.find(m => m.id === materialId);
    return material ? material.name : "";
  };

  const employeesByRole = groupEmployeesByRole(employees);

  const updateEmployeeEntry = (index, field, value) => {
    const updated = [...employeeEntries];
    updated[index][field] = value;
    setEmployeeEntries(updated);
  };

  const updateMaterial = (empIndex, matIndex, field, value) => {
    const updated = [...employeeEntries];
    updated[empIndex].materials[matIndex][field] = value;
    setEmployeeEntries(updated);
  };

  const resetForm = () => {
    setSelectedJob("");
    setNotes("");
    setEmployeeEntries(createEmptyEntries());
  };

  const handleSubmit = async () => {
    if (!selectedJob) {
      toast.error("Please select a job");
      return;
    }

    const validEntries = employeeEntries.filter(
      (e) => e.employee_id && parseFloat(e.hours) > 0
    );

    if (validEntries.length === 0) {
      toast.error("Please add at least one employee with hours");
      return;
    }

    setSubmitting(true);
    const payload = {
      job_id: selectedJob,
      date: selectedDate,
      notes: notes.slice(0, 200),
      employees: validEntries.map((e) => ({
        employee_id: e.employee_id,
        hours: parseFloat(e.hours),
        materials: e.materials
          .filter((m) => m.material_id && parseFloat(m.quantity) > 0)
          .map((m) => ({
            material_id: m.material_id,
            quantity: parseFloat(m.quantity),
          })),
      })),
    };
    try {
      if (editingEntryId) {
        await apiClient.put(`/daily-entries/${editingEntryId}`, payload);
        toast.success("Entry updated successfully!");
        setEditingEntryId(null);
      } else {
        await apiClient.post(`/daily-entries`, payload);
        toast.success("Daily entry submitted successfully!");
      }

      resetForm();
      fetchData();
    } catch (error) {
      if (!editingEntryId && isNetworkError(error)) {
        const jobName = jobs.find((j) => j.id === selectedJob)?.name || "this job";
        enqueueEntry(payload, jobName, user?.id);
        setPendingCount(readQueue().filter((q) => q.user_id === user?.id).length);
        toast.warning(`No signal — your entry for ${jobName} is saved on this phone and will send the next time you open the app.`, { duration: 10000 });
        showLocalNotification("Daily entry not sent yet", `Your entry for ${jobName} couldn't be sent. Just open the app again when you have signal — nothing else to do.`);
        resetForm();
        return;
      }
      toast.error(error.response?.data?.detail || "Failed to submit entry");
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditEntry = async (entry) => {
    setEditingEntryId(entry.id);
    setSelectedDate(entry.date);
    setSelectedJob(entry.job_id);
    setNotes(entry.notes || "");

    const formEntries = Array(8).fill(null).map((_, index) => {
      const emp = entry.employees?.[index];
      if (emp) {
        return {
          _uid: crypto.randomUUID(),
          employee_id: emp.employee_id,
          hours: emp.hours.toString(),
          materials: [
            ...(emp.materials || []).map((m) => ({
              _uid: crypto.randomUUID(),
              material_id: m.material_id,
              quantity: m.quantity.toString(),
            })),
            ...Array(Math.max(0, 3 - (emp.materials?.length || 0))).fill(null).map(() => ({
              _uid: crypto.randomUUID(),
              material_id: "",
              quantity: "",
            })),
          ].slice(0, 3),
        };
      }
      return {
        _uid: crypto.randomUUID(),
        employee_id: "",
        hours: "",
        materials: [
          { _uid: crypto.randomUUID(), material_id: "", quantity: "" },
          { _uid: crypto.randomUUID(), material_id: "", quantity: "" },
          { _uid: crypto.randomUUID(), material_id: "", quantity: "" },
        ],
      };
    });

    setEmployeeEntries(formEntries);
    setShowHistory(false);
    toast.info("Editing entry - make changes and save");
  };

  const handleCancelEdit = () => {
    setEditingEntryId(null);
    setSelectedDate(new Date().toISOString().split("T")[0]);
    resetForm();
  };

  const handleDeleteEntry = async (entryId) => {
    try {
      await apiClient.delete(`/daily-entries/${entryId}`);
      toast.success("Entry deleted");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete entry");
    }
  };

  const handlePhotoSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    if (!photoJob) {
      toast.error("Please select a job first");
      e.target.value = "";
      return;
    }

    const allowedExts = ["jpg", "jpeg", "png", "gif", "webp", "heic", "heif"];
    const validFiles = [];

    for (const file of files) {
      const ext = (file.name.split(".").pop() || "").toLowerCase();
      // iOS HEIC from the photo library often reports an empty or "image/heic"
      // MIME type, so validate by extension OR any image/* MIME (matches backend).
      const isImage = allowedExts.includes(ext) || (file.type || "").startsWith("image/");
      if (!isImage) {
        toast.error(`${file.name}: Only image files allowed`);
        continue;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name}: File too large (max 10MB)`);
        continue;
      }
      validFiles.push(file);
    }

    if (validFiles.length === 0) {
      e.target.value = "";
      return;
    }

    setPendingFiles(validFiles);
    setPhotoDescription("");
    setDescriptionDialogOpen(true);
    e.target.value = "";
  };

  const handlePhotoUpload = async () => {
    if (pendingFiles.length === 0) return;

    setUploading(true);
    setDescriptionDialogOpen(false);

    const isReceiptMode = uploadMode === "receipt";

    const formData = new FormData();
    pendingFiles.forEach(file => formData.append("files", file));
    if (photoDescription.trim()) {
      formData.append(isReceiptMode ? "description" : "caption", photoDescription.trim());
    }
    if (isReceiptMode) {
      // Pre-assign to the selected job, but hold for office reconciliation
      formData.append("job_id", photoJob);
      formData.append("reconciled", "false");
    }

    try {
      const uploadUrl = isReceiptMode
        ? `/receipts`
        : `/jobs/${photoJob}/photos`;

      const res = await apiClient.post(uploadUrl, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      const count = res.data.count || pendingFiles.length;
      setUploadedPhotoCount(count);
      if (user?.role === "foreman") sendPresencePing("photo_upload", true);
      setUploadConfirmOpen(true);
      setPhotoDescription("");
      setPendingFiles([]);

      if (res.data.errors?.length > 0) {
        res.data.errors.forEach(err => toast.error(err));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to upload ${isReceiptMode ? "receipts" : "photos"}`);
    } finally {
      setUploading(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr + "T00:00:00");
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    const y = date.getFullYear();
    const weekday = date.toLocaleDateString("en-US", { weekday: "short" });
    return `${weekday}, ${m}/${d}/${y}`;
  };

  const getJobName = (jobId) => {
    const job = jobs.find((j) => j.id === jobId);
    return job ? job.name : "Unknown Job";
  };

  return {
    // Data
    user, jobs, employees, materials, materialCategories, previousEntries,
    loading, submitting, showHistory, editingEntryId, employeesByRole,
    pendingCount, nearbyJobs,
    // Hours form state
    selectedDate, selectedJob, notes, employeeEntries,
    // Photo state
    photoJob, uploadMode, uploading, uploadConfirmOpen, uploadedPhotoCount,
    photoDescription, pendingFiles, descriptionDialogOpen,
    // Material picker state
    materialPickerOpen, selectedCategory,
    // Setters
    setShowHistory, setSelectedDate, setSelectedJob, setNotes,
    setPhotoJob, setUploadMode, setUploadConfirmOpen, setPhotoDescription,
    setDescriptionDialogOpen, setPendingFiles, setMaterialPickerOpen,
    setSelectedCategory,
    // Handlers
    updateEmployeeEntry, openMaterialPicker, selectMaterial,
    getMaterialsInCategory, getSelectedMaterialName, updateMaterial,
    handleSubmit, handleEditEntry, handleCancelEdit, handleDeleteEntry,
    handlePhotoSelect, handlePhotoUpload, formatDate, getJobName,
    logout,
  };
};
